<!--
  LoadersField: how many loaders, at the quote's rate. Shown once the
  customer adds the "+ Loaders" extra on the Details step (which sets the
  count to 1; its × sets it back to 0), and only for a quote that offers
  loaders (pricing offers them on medium and large vehicles once ops sets
  a rate). The stepper stays between 1 and the quote's max.

  The parent owns the count (v-model, 0 = no loaders) and adds
  count × rate to the total. The rate shown is the quote's, the same one
  orders charges, so the total on Confirm is what is billed.
-->
<script lang="ts" setup>
import { computed } from "vue";
import { Minus, Plus } from "@element-plus/icons-vue";

const props = defineProps<{
  modelValue: number;
  /** KES per loader, from the quote. */
  unitFee: number;
  /** Most loaders this quote allows. */
  max: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
}>();

const kes = (n: number) => `KES ${Number(n || 0).toLocaleString()}`;

const clamp = (n: number) => Math.max(1, Math.min(n, props.max));

const step = (delta: number) => emit("update:modelValue", clamp(props.modelValue + delta));

const subtotal = computed(() => props.modelValue * props.unitFee);
</script>

<template>
  <div class="lf">
    <div class="lf__row">
      <div class="lf__stepper" role="group" aria-label="Number of loaders">
        <button
          type="button"
          class="lf__step"
          aria-label="One loader fewer"
          :disabled="modelValue <= 1"
          @click="step(-1)"
        >
          <el-icon aria-hidden="true"><Minus /></el-icon>
        </button>
        <span class="lf__count" aria-live="polite">{{ modelValue }}<span class="sr-only"> {{ modelValue === 1 ? "loader" : "loaders" }}</span></span>
        <button
          type="button"
          class="lf__step"
          aria-label="One loader more"
          :disabled="modelValue >= max"
          @click="step(1)"
        >
          <el-icon aria-hidden="true"><Plus /></el-icon>
        </button>
      </div>
      <span class="lf__each">{{ kes(unitFee) }} each · <strong>+{{ kes(subtotal) }}</strong></span>
    </div>
    <p class="ods-help">The driver brings the loaders. Up to {{ max }}.</p>
  </div>
</template>

<style scoped src="./order-form.css"></style>
<style scoped>
.lf { font-family: 'DM Sans', sans-serif; }

.lf__row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.lf__stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
}
.lf__step {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #12284A;
  font-size: 16px;
  cursor: pointer;
}
.lf__step:hover:not(:disabled) { background: #f1f5f9; }
.lf__step:disabled { color: #cbd5e1; cursor: not-allowed; }
.lf__step:focus-visible { outline: 2px solid #2d43ff; outline-offset: -2px; }
.lf__count {
  min-width: 28px;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #12284A;
}
.lf__each { font-size: 0.8rem; color: #64748b; font-variant-numeric: tabular-nums; }
.lf__each strong { color: #12284A; font-weight: 600; }
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
